import { Children, type ReactNode, isValidElement, useRef } from 'react';
import styled from 'styled-components';

import { Box } from '@yoweb/ui/components/Box';
import { TabHorizontal, TabHorizontalContainer } from '@yoweb/ui/components/Tabs';
import type { TabHorizontalContainerProps } from '@yoweb/ui/components/Tabs/TabHorizontalContainer';
import { isArray } from '@yoweb/utils/isArray';
import type { TabProps } from '@yoweb/ui/components/Tabs/Tab';

type TabsProps = {
  children: ReactNode[];
  onChange?(label: string, activeIndex: number): void;
  ariaLabel?: string;
  activeTabId?: string;
  justify?: 'center' | 'flex-start';
  /**
   * If true hidden panels will have display: none but will always be rendered.
   */
  alwaysRenderTabs?: boolean;
  initialTab?: string;
  /**
   * Good when multiple <Tabs> components are rendered on the same page.
   */
  dataTestIdPrefix?: string;
} & Pick<TabHorizontalContainerProps, 'blendColor'>;

export const Tabs = ({
  children: childrenProp,
  ariaLabel,
  onChange,
  activeTabId,
  justify = 'flex-start',
  alwaysRenderTabs = true,
  initialTab,
  dataTestIdPrefix = '',
  blendColor,
}: TabsProps) => {
  const activeRef = useRef<HTMLButtonElement>(null);

  // childrenProp can have children that are undefined, this removes those children
  // and ensures correct rendering of the tabs
  const childTabs = isArray(childrenProp) ? childrenProp.filter(Boolean) : childrenProp;

  // Get Tabs label from Tabs children elements
  const labels = Children.map(childTabs, (child) => {
    if (!child) {
      return '';
    }

    if (!isValidElement(child)) throw new Error('Child must be a valid react element');

    return (child.props as TabProps)?.id || (child.props as TabProps)?.label;
  })?.filter(Boolean) as string[];

  const getActiveIndex = (value?: string) => {
    if (value) {
      const activeIndex = labels.findIndex((label) => value === label);

      if (activeIndex > -1) return activeIndex;
    }

    const initialTabIndex = initialTab ? labels.findIndex((label) => initialTab === label) : 0;

    return initialTabIndex > -1 ? initialTabIndex : 0;
  };

  const active = activeTabId ? getActiveIndex(activeTabId) : getActiveIndex();

  return (
    <div role="tablist" aria-label={ariaLabel}>
      <Box display="flex" justifyContent={justify}>
        <TabHorizontalContainer scrollTo={activeRef.current} blendColor={blendColor}>
          {Children.map(childTabs, (child, index) => {
            if (!child || !isValidElement(child)) {
              return null;
            }

            const { label, id } = child.props as TabProps;
            const isActive = active === index;

            return (
              <TabHorizontal
                key={id}
                isActive={isActive}
                size="sm"
                onClick={() => onChange?.(id, index)}
                role="tab"
                aria-selected={isActive}
                tabIndex={0}
                id={`tab-${index}`}
                aria-controls={`${dataTestIdPrefix}panel-${index}`}
                data-testid={`tab-${index}`}
              >
                {label}
              </TabHorizontal>
            );
          })}
        </TabHorizontalContainer>
      </Box>
      <Box mt="normal4">
        {Children.map(childTabs, (child, childIndex) => {
          if ((!alwaysRenderTabs && childIndex !== active) || !isValidElement(child)) {
            return null;
          }

          return (
            <Tab
              id={`panel-${childIndex}`}
              role="tabpanel"
              active={active === childIndex}
              data-testid={`${dataTestIdPrefix}panel-${childIndex}`}
            >
              {(child.props as TabProps).children}
            </Tab>
          );
        })}
      </Box>
    </div>
  );
};

// TODO: investigate if display: none / block toggling is still needed.
// May have been initial but I think consumers may expect the tab content to re-render
const Tab = styled.div<{ active: boolean }>`
  display: ${({ active }) => (active ? 'block' : 'none')};
  outline: none;
`;
